import { AppBar, Dialog, DialogContent, Input, Table, TableBody, TableCell, TableRow, Toolbar, Typography } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import SearchIcon from '@mui/icons-material/Search';
import genericListsStyles from 'assets/jss/genericListsStyles';
import { AppMessageBox } from 'components/AppMessageBox';
import BoxDrawer from 'components/BoxDrawer';
import IconButtonClose from 'components/Buttons/IconButtonClose';
import { SpinnerIndicator } from 'components/CallServer';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import MuiPushRight from 'components/MuiExtras/MuiPushRight';
import UsernameInspect from 'components/UsernameInspect';
import { GenericListItemDto, PagedResult } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type RenderColumnType = (item: GenericListItemDto) => React.ReactNode;

interface IProps {
    title: string;
    showThumbnail: boolean;
    variant?: "dialog" | "drawer";
    onGetData: (searchText: string, offset: number, take: number) => Promise<PagedResult<GenericListItemDto>>;
    onRenderThumbnail?: RenderColumnType;
    onRenderName?: RenderColumnType;
    onRenderSecondLine?: RenderColumnType;
    onClose: () => void;
}

interface IState {
    ds: PagedResult<GenericListItemDto>;
    page: number;
    loading: boolean,
    loadingError: boolean,
}

type Props = IProps & WithTranslation & WithStyles<typeof genericListsStyles>;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const rowsPerPage = 10;

class GenericList extends React.Component<Props, IState> {

    private searchRef: any = null;

    static defaultProps: Partial<IProps> = {
        variant: "dialog",
        onRenderThumbnail: (item: GenericListItemDto) => <img src={item.thumbnailUrl} alt={item.name} />,
        onRenderName: (item: GenericListItemDto) => <UsernameInspect user={item} />
    };

    constructor(props: Props) {
        super(props);


        this.state = {
            ds: { items: [], total: 0 },
            page: 0,
            loading: true,
            loadingError: false,
        };
    }

    private getData(page: number) {
        const searchText: string = this.searchRef ? this.searchRef.value : null;

        this.setState({ loading: true, loadingError: false, page: page });

        this.props.onGetData(searchText, page * rowsPerPage, rowsPerPage)
            .then((data) => {
                this.setState({ ds: data, loading: false, loadingError: false });
            })
            .catch(() => {
                this.setState({ loading: false, loadingError: true });
            });
    }

    private handleSearch(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (e.keyCode == 13) this.getData(0);
    }

    private renderContent() {
        const { classes, showThumbnail, onRenderName, onRenderThumbnail, onRenderSecondLine } = this.props;
        const { ds, page, loading, loadingError } = this.state;

        if (loadingError) {
            return <AppMessageBox variant="error" resource="LoadListDataFailed" />;
        }

        return (
            <GridEx container fullHeight direction="column" wrap="nowrap">
                <GridEx>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <SearchIcon />
                        </div>
                        <Input
                            placeholder={this.props.t("Common.Input.Placeholder.SearchFor")}
                            disableUnderline
                            onKeyDown={(e) => this.handleSearch(e)}
                            inputRef={(ref) => this.searchRef = ref}
                            classes={{
                                root: classes.inputRoot,
                                input: classes.inputInput,
                            }}
                        />
                    </div>

                </GridEx>
                <GridEx item xs>
                    <MuiContainer margin="dense">
                        {
                            loading
                                ? <SpinnerIndicator />
                                : (
                                    <Table className={classes.table}>
                                        <TableBody>
                                            {
                                                ds.items.map(n => {
                                                    return (
                                                        <TableRow
                                                            hover
                                                            tabIndex={-1}
                                                            key={n.id}
                                                        >
                                                            {
                                                                showThumbnail &&
                                                                <TableCell padding="none" style={{ width: "1px", padding: 4 }}>
                                                                    {onRenderThumbnail(n)}
                                                                </TableCell>
                                                            }

                                                            <TableCell padding="none">
                                                                {onRenderName(n)}
                                                                {onRenderSecondLine && <><br />{onRenderSecondLine(n)}</>}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            }
                                        </TableBody>
                                    </Table>
                                )
                        }
                    </MuiContainer>
                </GridEx>
                <GridEx item>
                    <MuiPagination
                        limit={rowsPerPage}
                        offset={page * rowsPerPage}
                        total={ds.total}
                        onClick={(e, offset, page) => this.getData(page - 1)}
                    />
                </GridEx>
            </GridEx>
        );
    }


    private renderDialog() {
        const { title } = this.props;
        return (
            <Dialog
                open
                fullWidth
                maxWidth={"md"}
                onClose={() => { this.props.onClose(); }}
            >
                <DialogContent style={{ margin: 0, padding: 0 }}>

                    <AppBar position={"static"}>
                        <Toolbar>
                            <Typography variant="h6" color="inherit" noWrap>
                                {title}
                            </Typography>
                            <MuiPushRight>
                                <IconButtonClose onClick={() => this.props.onClose()} />
                            </MuiPushRight>
                        </Toolbar>
                    </AppBar>

                    <MuiPaper className={this.props.classes.tableContainer}>
                        {this.renderContent()}
                    </MuiPaper>

                </DialogContent>
            </Dialog >
        );
    }

    private renderDrawer() {
        const { title } = this.props;
        return (
            <BoxDrawer
                open
                title={title}
                overflow="auto"
                onClose={() => { this.props.onClose(); }}
            >
                <MuiPaper fullHeight elevation={0} >
                    {this.renderContent()}
                </MuiPaper>
            </BoxDrawer>
        );
    }

    componentDidMount() {
        this.getData(0);
    }

    render() {
        return this.props.variant == "dialog"
            ? this.renderDialog()
            : this.renderDrawer();
    }
}

export default withTranslation()(withStyles(genericListsStyles)(GenericList));